import React, { useMemo } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';

import { PersonalisationCard } from '../PersonalisationCard';

import styles from '../PersonalisationStep/personalisation-grid.module.css';

/**
 * ComponentGroup component displays a component group in the personalisation journey.
 *
 * @param {Object} props - Component props
 * @param {Object} props.group - The group object
 * @param {Object} props.slot - The slot object
 * @param {string} props.slotId - The ID of the slot
 * @param {boolean=} props.showUiHeading - Whether to show the UI heading
 * @returns {import('react').JSX.Element | null}
 */
export const ComponentGroup = ({ group, slot, slotId, showUiHeading = false }) => {
  const { variants } = usePersonalisationContext();

  // If the group product variant have the groupHero key then use that to filter the group.
  // Otherwise let all the variants be displayed.
  let processedGroup = useMemo(
    () =>
      group.productVariantIds
        .map((id) => variants.get(id))
        .filter((variant) => {
          // Check if the variant does not have secondary options or if the group hero is true
          return variant && (variant.secondaryOptions.length === 0 || variant.groupHero !== false);
        }),
    [group.productVariantIds, variants]
  );

  // Default behavior for regular component groups (one card per variant)
  return (
    <fieldset>
      {showUiHeading && (
        <legend className={styles['personalisation__selection-grid-heading'] + ' body-base font-medium'}>
          {group.uiHeading}
        </legend>
      )}
      <ul
        className={
          styles['personalisation__selection-grid'] +
          ' ' +
          styles[`personalisation__selection-grid--${slot.columns}-column`]
        }
      >
        {processedGroup.map(
          ({
            id,
            title,
            image,
            price,
            secondaryOptionsName,
            secondaryOptions,
            secondaryOptionsLabel,
            disableSecondaryOptions,
            product,
            availableForSale,
            shortDescription
          }) => {
            // Use product title if variant title is "Default Title"
            const productTitle = title === 'Default Title' && product?.title ? product.title : title;

            // Checks if the group is available for sale.
            // If there are no secondary options, check the main variant's availability.
            // If there are secondary options, check if any of them are available for sale.
            const isGroupAvailable =
              secondaryOptions.length === 0
                ? availableForSale
                : secondaryOptions.some((option) => option.availableForSale);

            // Don't show the short description if the secondary options exist.
            // Secondary option cards will display the short description individually.
            const showShortDescription = disableSecondaryOptions !== false;

            return (
              <PersonalisationCard
                id={id}
                slotId={slotId}
                componentGroupId={group.id}
                key={id}
                imageSrc={image?.url}
                imageAlt={image?.alt || productTitle}
                title={disableSecondaryOptions ? secondaryOptionsName : productTitle}
                description={showShortDescription ? shortDescription?.value || null : null}
                variants={group.productVariantIds}
                price={price}
                secondaryOptions={secondaryOptions}
                secondaryOptionsName={secondaryOptionsName}
                secondaryOptionsLabel={secondaryOptionsLabel}
                disableSecondaryOptions={disableSecondaryOptions}
                selectedVariantId={id}
                isSelected={false}
                badge={group.badge}
                helpPage={group.helpPage}
                availableForSale={isGroupAvailable}
                priceModifierType={group.priceModifierType}
                priceModifierValue={group.priceModifierValue}
              />
            );
          }
        )}
      </ul>
    </fieldset>
  );
};
